import { useMemo, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import { Modal, toast } from '../ui'
import type { Take, Transcript } from '../types'
import { clock, cls, paragraphs, speakerName, type Interview } from '../util'

/*
  One long recording (a stringout of several people, two days joined) into one interview per
  person. The team clicks "Split here" between paragraphs and names each part. Suggestions:
  someone introducing themselves ("My name is…") and long pauses (a new person, a new day).
  The server keeps the same files on a shifted clock, so nothing is re-exported or re-transcribed.
*/

const INTRO = /\b(?:[Mm]y name is|[Mm]y name's|I'm|I am)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/
const NOT_NAMES = new Set(['The', 'So', 'Just', 'Really', 'Here', 'Not', 'Going', 'Very', 'From', 'In', 'At', 'On', 'A', 'An', 'And', 'But', 'Also', 'Still', 'Now', 'Actually', 'Super', 'Pretty', 'Basically'])
const LONG_PAUSE = 90

type Para = { i: number; start: number; end: number; speaker: string; text: string; hint?: string; name?: string }

export function SplitDialog({ projectId, iv, transcripts, onClose, onDone }: { projectId: string; iv: Interview; transcripts: Record<string, Transcript>; onClose: () => void; onDone: () => void }) {
  const longest = [...iv.takes].sort((a, b) => b.duration - a.duration)[0]
  const [takeId, setTakeId] = useState(longest.id)
  const take = iv.takes.find((t) => t.id === takeId) || longest
  const paras = useMemo(() => buildParas(take, transcripts[take.id]), [take, transcripts])
  const [cuts, setCuts] = useState<Set<number>>(new Set())
  const [names, setNames] = useState<Record<number, string>>({})
  const [busy, setBusy] = useState(false)
  const [q, setQ] = useState('')
  const list = useRef<HTMLDivElement>(null)
  const starts = [0, ...[...cuts].sort((a, b) => a - b)]
  const parts = starts.map((p, k) => {
    const next = starts[k + 1]
    const from = k === 0 ? 0 : cutTime(paras, p)
    const to = next == null ? take.duration : cutTime(paras, next)
    const fallback = paras[p]?.name || (k === 0 ? iv.name : `${iv.name} ${k + 1}`)
    return { p, from, to, name: names[p] ?? fallback }
  })
  const toggle = (i: number) => {
    setCuts((cur) => {
      const n = new Set(cur)
      n.has(i) ? n.delete(i) : n.add(i)
      return n
    })
  }
  const jump = (text: string) => {
    setQ(text)
    const t = text.trim().toLowerCase()
    if (t.length < 2) return
    const hit = paras.find((p) => p.text.toLowerCase().includes(t))
    if (hit) list.current?.querySelector(`[data-para="${hit.i}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }
  const apply = async () => {
    if (parts.some((x) => !x.name.trim())) return toast('Name every part.', 'err')
    setBusy(true)
    try {
      await api.post(`p/${projectId}/takes/${take.id}/split`, { at: parts.slice(1).map((x) => x.from), names: parts.map((x) => x.name.trim()) })
      toast(`Split into ${parts.length} interviews.`)
      onDone()
      onClose()
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not split', 'err')
      setBusy(false)
    }
  }
  const busyTake = take.status === 'queued' || take.status === 'processing'
  return (
    <Modal title="Split into people" onClose={onClose} wide>
      <div className="explain small">
        <b>Several people in one long recording?</b>
        <p>Click <b>Split here</b> where each new person starts, and name each part. Each part becomes its own interview with its own video, transcript and marks. Nothing gets re-exported or re-transcribed, and any cameras you add later line up with every part.</p>
      </div>
      {iv.takes.length > 1 && (
        <div className="split-take small">
          Recording:{' '}
          <select value={take.id} onChange={(e) => { setTakeId(e.target.value); setCuts(new Set()); setNames({}) }}>
            {iv.takes.map((t) => <option key={t.id} value={t.id}>{t.name} ({clock(t.duration)})</option>)}
          </select>
        </div>
      )}
      <div className="split-parts">
        {parts.map((x, k) => (
          <label key={x.p} className="split-part">
            <span className="split-n">{k + 1}</span>
            <input value={x.name} onChange={(e) => setNames((n) => ({ ...n, [x.p]: e.target.value }))} aria-label={`Name of part ${k + 1}`} maxLength={120} />
            <span className="muted small nowrap">{clock(x.from)} to {clock(x.to)}</span>
          </label>
        ))}
      </div>
      <div className="split-tools">
        <input className="split-find" value={q} onChange={(e) => jump(e.target.value)} placeholder="Find a word or name to jump to it" aria-label="Find in transcript" />
        <span className="muted small">{paras.filter((p) => p.hint && p.i > 0).length ? `${paras.filter((p) => p.hint && p.i > 0).length} suggested spots marked` : 'No obvious spots found, look for where each person starts'}</span>
      </div>
      {busyTake ? (
        <p className="muted pad">This recording is still being transcribed. Split it once the transcript is done.</p>
      ) : (
        <div className="split-list" ref={list}>
          {paras.map((p) => (
            <div key={p.i} data-para={p.i}>
              {p.i > 0 && (
                <button className={cls('split-gap', cuts.has(p.i) && 'on', p.hint && 'hint')} onClick={() => toggle(p.i)}>
                  <span className="split-line" />
                  <span className="split-label">{cuts.has(p.i) ? `Part ${starts.indexOf(p.i) + 1} starts here (undo)` : p.hint ? `Split here? ${p.hint}` : 'Split here'}</span>
                  <span className="split-line" />
                </button>
              )}
              <div className={cls('split-para', q.trim().length > 1 && p.text.toLowerCase().includes(q.trim().toLowerCase()) && 'match')}>
                <span className="muted small nowrap">{clock(p.start)}</span>
                <div className="grow">
                  {p.speaker && <b className="small">{p.speaker} </b>}
                  <span className="small">{p.text}</span>
                </div>
              </div>
            </div>
          ))}
          {!paras.length && <p className="muted pad">No transcript yet, so there's nothing to split by.</p>}
        </div>
      )}
      <div className="form-actions">
        <span className="muted small grow">{parts.length > 1 ? `${parts.length} interviews` : 'Pick at least one place to split.'}</span>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy || parts.length < 2 || busyTake} onClick={apply}>{busy ? 'Splitting…' : parts.length < 2 ? 'Split' : `Split into ${parts.length}`}</button>
      </div>
    </Modal>
  )
}

/** Cut just before the new part's first words (up to 2 s of lead-in, never into the last part's words). */
function cutTime(paras: Para[], i: number) {
  const prev = paras[i - 1]
  const cur = paras[i]
  if (!prev) return cur.start
  const lead = Math.min(2, Math.max(0, (cur.start - prev.end) / 2))
  return Math.round((cur.start - lead) * 1000) / 1000
}

function buildParas(take: Take, tr: Transcript | undefined): Para[] {
  const segs = tr?.segments || []
  const ps = paragraphs(segs)
  return ps.map((p, i) => {
    const words = p.segs.flatMap((k) => segs[k].words.map((w) => w[0]))
    const text = words.slice(0, 60).join(' ') + (words.length > 60 ? '…' : '')
    const out: Para = { i, start: p.start, end: p.end, speaker: p.speaker && p.speaker !== '?' ? speakerName(take, p.speaker) : '', text }
    const m = words.slice(0, 40).join(' ').match(INTRO)
    const name = m?.[1].split(/\s+/)[0]
    if (m && name && !NOT_NAMES.has(name)) {
      out.hint = `"${m[0]}"`
      out.name = m[1]
    }
    const gap = i > 0 ? p.start - ps[i - 1].end : 0
    if (gap >= LONG_PAUSE) out.hint = (out.hint ? out.hint + ', ' : '') + `${Math.round(gap / 60) || 1} min pause before this`
    return out
  })
}
